import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Box, Breadcrumbs, Typography } from '@mui/material';
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { ROUTING_NAMES } from '../constants/routingConstant';

const BreadcrumbNavigation = ({ customBreadcrumbs = null }) => {
  const location = useLocation();
  
  // Map routes to readable names
  const routeNameMap = {
    '/': 'Home',
    [ROUTING_NAMES.aboutUs]: 'About Us',
    [ROUTING_NAMES.features]: 'Features',
    [ROUTING_NAMES.pricing]: 'Pricing',
    [ROUTING_NAMES.services]: 'Services',
    [ROUTING_NAMES.contact_us]: 'Contact Us',
    [ROUTING_NAMES.partners]: 'Become a Partner',
    [ROUTING_NAMES.careers]: 'Careers',
    [ROUTING_NAMES.blogs]: 'Blog',
    [ROUTING_NAMES.privacyPolicy]: 'Privacy Policy',
    [ROUTING_NAMES.termAndConditions]: 'Terms & Conditions',
    [ROUTING_NAMES.refundPolicy]: 'Refund Policy',
    [ROUTING_NAMES.security]: 'Security',
    [ROUTING_NAMES.govermentCertified]: 'Government Certified'
  };

  // Generate breadcrumbs from current path
  const generateBreadcrumbs = () => {
    if (customBreadcrumbs) {
      return customBreadcrumbs;
    }

    const pathSegments = location.pathname.split('/').filter(segment => segment);
    const breadcrumbs = [{ name: 'Home', url: '/' }];

    if (pathSegments.length > 0) {
      const currentPath = location.pathname;
      const currentName = routeNameMap[currentPath] || pathSegments[pathSegments.length - 1];
      
      if (currentName !== 'Home') {
        breadcrumbs.push({
          name: currentName,
          url: currentPath
        });
      }
    }

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  // Don't show breadcrumbs on home page unless there are custom breadcrumbs
  if (location.pathname === '/' && !customBreadcrumbs) {
    return null;
  }

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((breadcrumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": breadcrumb.name,
      "item": `https://ekalsutra.com${breadcrumb.url}`
    }))
  };

  return (
    <>
      {/* Structured Data for Breadcrumbs */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      
      <Box 
        sx={{ 
          py: 2, 
          px: { xs: 2, md: 4 },
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #e9ecef'
        }}
      >
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{
            '& .MuiBreadcrumbs-separator': {
              color: '#6c757d'
            }
          }}
        >
          {breadcrumbs.map((breadcrumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            
            return isLast ? (
              <Typography 
                key={breadcrumb.url}
                color="primary"
                sx={{ 
                  fontWeight: 600,
                  fontSize: '0.875rem'
                }}
                aria-current="page"
              >
                {breadcrumb.name}
              </Typography>
            ) : (
              <Link
                key={breadcrumb.url}
                to={breadcrumb.url}
                style={{
                  textDecoration: 'none',
                  color: '#6c757d',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.color = '#0C2F49';
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = '#6c757d';
                }}
              >
                {breadcrumb.name}
              </Link>
            );
          })}
        </Breadcrumbs>
      </Box>
    </>
  );
};

export default BreadcrumbNavigation;
